import { Fragment, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../../lib/utils";
import { PanelToast } from "../../../patterns/panel-toast";
import { Text } from "../../../primitives";
import { useDecisioningData } from "./DecisioningDataContext";
import {
	type DecisioningSheet,
	type DecisioningTab,
	useDecisioningPanel,
} from "./DecisioningPanelContext";
import { DataNeededTab } from "./data-needed/DataNeededTab";
import { RequestDataSheet } from "./data-needed/RequestDataSheet";
import { ResolveDataNeededSheet } from "./data-needed/ResolveDataNeededSheet";
import { ConnectFactorSheet } from "./factors/ConnectFactorSheet";
import { FactorSheet } from "./factors/FactorSheet";
import { FactorsTab } from "./factors/FactorsTab";
import { HistoryTab } from "./history/HistoryTab";
import { LogDecisionSheet } from "./history/LogDecisionSheet";
import { OverviewTab } from "./overview/OverviewTab";

const TABS: readonly DecisioningTab[] = ["overview", "factors", "data-needed", "history"];
const tabId = (tab: DecisioningTab) => `decisioning-tab-${tab}`;
const PANEL_ID = "decisioning-tabpanel";

const TRACK_CLASSES = "flex w-full items-center gap-1 rounded-full bg-surface-neutral p-1";
const TAB_CLASSES =
	"flex flex-auto items-center justify-center whitespace-nowrap rounded-full py-0.5 text-sm leading-[20px] font-normal text-secondary cursor-pointer select-none transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-0 focus-visible:ring-brand-accent focus-visible:ring-opacity-30 aria-selected:bg-surface-base aria-selected:font-semibold aria-selected:text-primary aria-selected:shadow";

const COUNTS_MIN_WIDTH = 400;
const FULL_LABEL_MIN_WIDTH = 336;

const TAB_CONTENT: Record<DecisioningTab, () => ReactNode> = {
	overview: () => <OverviewTab />,
	factors: () => <FactorsTab />,
	"data-needed": () => <DataNeededTab />,
	history: () => <HistoryTab />,
};

const OpenDot = ({ crucial, count }: { crucial: boolean; count: number }) => (
	<>
		<span
			aria-hidden
			data-testid="decisioning-tab-open-dot"
			className={cn(
				"ml-1 inline-block size-1.5 rounded-full",
				crucial ? "bg-danger" : "bg-brand-accent",
			)}
		/>
		<span className="sr-only" data-testid="decisioning-tab-open-dot-label">
			{`, ${count} open${crucial ? ", including crucial" : ""}`}
		</span>
	</>
);

const TabLabel = ({ label, count }: { label: string; count?: number }) => (
	<span className="tabular-nums">
		{label}
		{count !== undefined && (
			<Text type="caption" color="tertiary" className="ml-1 tabular-nums">
				{count}
			</Text>
		)}
	</span>
);

const DecisioningTabs = () => {
	const { tab, setTab, closeSheet, panelWidth } = useDecisioningPanel();
	const { proCons, dataNeeded, decisions, decisionsLoaded } = useDecisioningData();
	const showCounts = panelWidth >= COUNTS_MIN_WIDTH;
	const dataLabel = panelWidth >= FULL_LABEL_MIN_WIDTH ? "Data needed" : "Data";
	const crucialOpen = dataNeeded.some((item) => item.importance === 3);
	const changeTab = (next: DecisioningTab) => {
		closeSheet();
		setTab(next);
	};

	/** Arrow keys, Home and End move between tabs and select them, per the ARIA tabs pattern. */
	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		const index = TABS.indexOf(tab);
		const next = {
			ArrowRight: TABS[(index + 1) % TABS.length],
			ArrowLeft: TABS[(index - 1 + TABS.length) % TABS.length],
			Home: TABS[0],
			End: TABS[TABS.length - 1],
		}[event.key];
		if (next === undefined) return;
		event.preventDefault();
		changeTab(next);
		event.currentTarget.querySelector<HTMLElement>(`#${tabId(next)}`)?.focus();
	};

	const labels: Record<DecisioningTab, ReactNode> = {
		overview: <TabLabel label="Overview" />,
		factors: <TabLabel label="Factors" count={showCounts ? proCons.length : undefined} />,
		"data-needed": (
			<span className="inline-flex items-center">
				<TabLabel label={dataLabel} count={showCounts ? dataNeeded.length : undefined} />
				{!showCounts && dataNeeded.length > 0 && (
					<OpenDot crucial={crucialOpen} count={dataNeeded.length} />
				)}
			</span>
		),
		history: (
			<TabLabel
				label="History"
				count={showCounts && decisionsLoaded ? decisions.length : undefined}
			/>
		),
	};

	return (
		<div className="border-b border-bc-subtle px-4 py-2.5">
			<div
				role="tablist"
				aria-label="Decisioning sections"
				className={TRACK_CLASSES}
				onKeyDown={handleKeyDown}
			>
				{TABS.map((value) => (
					<button
						key={value}
						type="button"
						role="tab"
						id={tabId(value)}
						aria-selected={tab === value}
						aria-controls={PANEL_ID}
						tabIndex={tab === value ? 0 : -1}
						className={cn(TAB_CLASSES, showCounts ? "px-2" : "px-1.5")}
						data-testid={tabId(value)}
						onClick={() => changeTab(value)}
					>
						{labels[value]}
					</button>
				))}
			</div>
		</div>
	);
};

const sheetKey = (sheet: DecisioningSheet): string => {
	switch (sheet.kind) {
		case "factor":
			return sheet.mode === "edit"
				? `factor:edit:${sheet.proConId}`
				: `factor:add:${sheet.side}:${sheet.importance ?? ""}`;
		case "request":
			return sheet.mode === "edit"
				? `request:edit:${sheet.dataNeededId}`
				: `request:add:${sheet.linkedProConId ?? ""}`;
		case "resolve":
			return `resolve:${sheet.dataNeededId}`;
		case "connect":
			return `connect:${sheet.proConId}`;
		case "log":
			return `log:${sheet.supersedesDecisionId ?? "new"}:${sheet.stage ?? ""}`;
	}
};

const renderSheet = (sheet: DecisioningSheet): ReactNode => {
	switch (sheet.kind) {
		case "factor":
			return <FactorSheet sheet={sheet} />;
		case "request":
			return <RequestDataSheet sheet={sheet} />;
		case "resolve":
			return <ResolveDataNeededSheet sheet={sheet} />;
		case "connect":
			return <ConnectFactorSheet sheet={sheet} />;
		case "log":
			return <LogDecisionSheet sheet={sheet} />;
	}
};

const SheetLayer = () => {
	const { sheet } = useDecisioningPanel();
	if (!sheet) return null;
	return <Fragment key={sheetKey(sheet)}>{renderSheet(sheet)}</Fragment>;
};

export const DecisioningPanelBody = () => {
	const { tab, scrollRef, toast, dismissToast } = useDecisioningPanel();

	return (
		<div className="flex h-full min-h-0 flex-col" data-testid="decisioning-panel">
			<DecisioningTabs />
			<div className="relative min-h-0 flex-1">
				<div
					ref={scrollRef}
					id={PANEL_ID}
					role="tabpanel"
					aria-labelledby={tabId(tab)}
					className="h-full overflow-y-auto scrollbar-slim"
				>
					<div className="flex flex-col gap-4 px-4 pb-7 pt-3">{TAB_CONTENT[tab]()}</div>
				</div>
				<SheetLayer />
				<PanelToast toast={toast} onDismiss={dismissToast} />
			</div>
		</div>
	);
};
